import { TransactionStatus } from '../transaction-status';
import { TooltipProvider } from '../ui/tooltip';
import { AuthProvider } from './auth-provider';
import { MockAuthProvider } from './mock-auth-provider';
import { MockWalletProvider } from './mock-wallet-provider';
import { WalletProvider } from './wallet-provider';
import { IS_DEBUG, IS_E2E, wagmiConfig } from '@/client/confs/app';
import { useTheme } from '@/client/hooks/use-theme';
import { ReactQueryDevtoolsProduction } from '@/client/utils/dev';
import { persistOptions, queryClient } from '@/client/utils/query-client';
import { isSiteSwitchSignedOut } from '@/client/utils/site-switch';
import { RainbowKitProvider, darkTheme, lightTheme } from '@rainbow-me/rainbowkit';
import '@rainbow-me/rainbowkit/styles.css';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { type ReactNode, useEffect, useState } from 'react';
import useMedia from 'react-use/lib/useMedia';
import { WagmiProvider, useAccount, useConnect } from 'wagmi';

// Check if mock mode is enabled (legacy, kept for backwards compat)
const USE_MOCK = import.meta.env.VITE_ENABLE_MOCK === 'true';

const ReactQueryDevtools = () => {
  const isDesktop = useMedia('(min-width: 768px)');
  if (IS_DEBUG) {
    return <ReactQueryDevtoolsProduction initialIsOpen={false} buttonPosition={isDesktop ? 'bottom-left' : 'bottom-left'} />;
  }
  return null;
};

// Auto-connect the E2E test connector on mount
const E2EAutoConnect = () => {
  const { isConnected } = useAccount();
  const { connect, connectors } = useConnect();

  useEffect(() => {
    if (!isConnected && connectors.length > 0) {
      connect({ connector: connectors[0] });
    }
  }, [isConnected, connect, connectors]);

  return null;
};

export const AppProviders = ({ children }: { children: ReactNode }) => {
  const { theme } = useTheme();
  const [reconnectOnMount] = useState(() => !isSiteSwitchSignedOut());

  // Legacy mock mode (will be removed in cleanup PR)
  if (USE_MOCK && !IS_E2E) {
    return (
      <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
        <ReactQueryDevtools />
        <MockWalletProvider>
          <MockAuthProvider>
            <TooltipProvider delayDuration={100}>{children}</TooltipProvider>
          </MockAuthProvider>
        </MockWalletProvider>
      </PersistQueryClientProvider>
    );
  }

  return (
    <WagmiProvider config={wagmiConfig} reconnectOnMount={reconnectOnMount}>
      <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
        <RainbowKitProvider
          theme={theme === 'dark' ? darkTheme({ accentColor: '#36383a' }) : lightTheme({ accentColor: '#36383a' })}
        >
          <ReactQueryDevtools />
          {IS_E2E && <E2EAutoConnect />}
          <WalletProvider>
            <AuthProvider>
              <TooltipProvider delayDuration={100}>
                {children}
                <TransactionStatus />
              </TooltipProvider>
            </AuthProvider>
          </WalletProvider>
        </RainbowKitProvider>
      </PersistQueryClientProvider>
    </WagmiProvider>
  );
};

export default AppProviders;
